import {
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps
} from "@mui/material";

import { useCallback, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { selectAutostoreGridIdOverride } from "~/redux/selectors/inventorySelectors";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { AutostoreGridDto } from "~/types/api";

import { setAutostoreGridIdOverride } from "./inventory.slice";

/** Allows users without a workstation to select an Autostore Grid */
export function useGridSelector(props?: SelectProps<string | undefined>) {
  const trackedPageName = useMixpanelPageName();
  const autostoreGridOverride = useAppSelector(selectAutostoreGridIdOverride);

  const userFc = useAppSelector((state) => state.store.usersFulfillmentCenter);

  const { data: autostoreGrids } = useGetAutostoreGridsQuery(undefined, {
    skip: !userFc
  });
  const dispatch = useAppDispatch();

  const getGridName = useCallback(
    (gridId: string | undefined) =>
      autostoreGrids?.find((g) => g.autostoreGridId === gridId)
        ?.autostoreGridName,
    [autostoreGrids]
  );

  const handleGridChange = useCallback(
    (e: SelectChangeEvent) => {
      const selectedGrid = e.target.value || undefined;
      const gridName = getGridName(selectedGrid);

      mixpanelTrack({
        trackedPageName,
        label: "Select Autostore Grid",
        type: "Select Option",
        eventProperties: {
          "Grid Name": gridName ?? "No Grid Selected"
        }
      });

      dispatch(setAutostoreGridIdOverride(selectedGrid));
    },
    [dispatch, getGridName, trackedPageName]
  );

  const gridSelectorButton = useMemo(
    () => (
      <Select
        value={autostoreGridOverride || ""}
        onChange={handleGridChange}
        size="small"
        variant="outlined"
        displayEmpty
        {...props}
      >
        <MenuItem key={"all-grids"} value="">
          No Grid Selected
        </MenuItem>
        {(autostoreGrids || []).map(
          ({ autostoreGridName, autostoreGridId }: AutostoreGridDto) => (
            <MenuItem key={autostoreGridId} value={autostoreGridId}>
              {autostoreGridName}
            </MenuItem>
          )
        )}
      </Select>
    ),
    [autostoreGridOverride, autostoreGrids, handleGridChange, props]
  );

  return { autostoreGridOverride, gridSelectorButton };
}
